import { cn } from '@/lib/utils';

/**
 * Bascule de planning (calendrier) sous forme de segmenté coloré.
 *
 * Chaque calendrier porte une couleur (`gym_calendars.color`) ; on l'utilise
 * pour teinter la pastille et l'onglet actif, ce qui rend le planning
 * immédiatement lisible (« je suis sur Cours Privés »). Pensé pour l'espace
 * adhérent qui détient plusieurs abonnements de plannings différents, mais
 * réutilisable partout (admin, coach).
 *
 * `selectedCalendarId === null` = « Tous les plannings ».
 */

export interface SwitchableCalendar {
  id: string;
  name: string;
  color?: string | null;
}

interface CalendarSwitcherProps {
  calendars: SwitchableCalendar[];
  selectedCalendarId: string | null;
  onSelect: (id: string | null) => void;
  allLabel?: string;
  ariaLabel?: string;
  className?: string;
}

// Repli de couleurs quand un calendrier n'a pas de `color` défini. Teintes
// franches et distinctes, agréables en clair comme en sombre.
const FALLBACK_PALETTE = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444'];

function calendarColor(cal: SwitchableCalendar, index: number): string {
  const c = (cal.color ?? '').trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c) ? c : FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

/** Texte lisible (blanc ou quasi-noir) au-dessus d'une couleur de fond donnée. */
function readableText(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Luminance relative perçue (sRGB approx).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#111827' : '#ffffff';
}

export function CalendarSwitcher({
  calendars,
  selectedCalendarId,
  onSelect,
  allLabel = 'Tous',
  ariaLabel = 'Choisir un planning',
  className,
}: CalendarSwitcherProps) {
  const options = [
    { id: null as string | null, name: allLabel, color: null as string | null },
    ...calendars,
  ];

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full',
        'bg-muted/60 p-1 shadow-sm ring-1 ring-border/60 backdrop-blur-sm',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {options.map((opt, index) => {
        const isActive = selectedCalendarId === opt.id;
        const isAll = opt.id === null;
        const color = isAll ? null : calendarColor(opt as SwitchableCalendar, index - 1);

        return (
          <button
            key={opt.id ?? '__all__'}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(opt.id)}
            className={cn(
              'group relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full',
              'px-3.5 py-1.5 text-sm font-medium outline-none transition-all duration-200',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              isActive
                ? 'shadow-md'
                : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
            )}
            style={
              isActive
                ? isAll
                  ? undefined
                  : { backgroundColor: color!, color: readableText(color!) }
                : undefined
            }
          >
            {/* Onglet « Tous » actif : dégradé arc-en-ciel discret, sinon la
                pastille prend la couleur du calendrier. */}
            {isActive && isAll && (
              <span className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-teal-400 opacity-90" />
            )}
            <span
              className={cn(
                'h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/5 transition-transform',
                'group-hover:scale-110'
              )}
              style={
                isAll
                  ? {
                      background:
                        'conic-gradient(from 0deg, #6366f1, #ec4899, #14b8a6, #f59e0b, #6366f1)',
                    }
                  : {
                      backgroundColor: color!,
                      boxShadow: isActive ? 'inset 0 0 0 1.5px rgba(255,255,255,0.75)' : undefined,
                    }
              }
            />
            <span className={cn(isActive && isAll && 'text-white')}>{opt.name}</span>
          </button>
        );
      })}
    </div>
  );
}
